import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react'
import { TX_PLAN_HISTORY_MAX_SNAPSHOTS } from './TreatmentPlanMain.constants'
import { cloneTxPlanSnapshot } from './treatmentPlanMain.helpers'
import type {
  FormStateSnapshot,
  TxPlanHistoryState,
  TxPlanTabFormState,
} from './TreatmentPlanMain.types'

export type UseTreatmentPlanHistoryParams = {
  formStates: Record<string, TxPlanTabFormState>
  formStatesRef: MutableRefObject<Record<string, TxPlanTabFormState>>
  localDrafts: Record<string, boolean>
  localDraftsRef: MutableRefObject<Record<string, boolean>>
  editModes: Record<string, boolean>
  editModesRef: MutableRefObject<Record<string, boolean>>
  phaseVisibilityRef: MutableRefObject<Record<string, boolean>>
  setFormStates: Dispatch<SetStateAction<Record<string, TxPlanTabFormState>>>
  setPhaseVisibility: Dispatch<SetStateAction<Record<string, boolean>>>
}

export type UseTreatmentPlanHistoryResult = {
  canUndo: (tabKey: string) => boolean
  canRedo: (tabKey: string) => boolean
  handleUndo: (tabKey: string) => void
  handleRedo: (tabKey: string) => void
  saveHistorySnapshot: (tabKey: string) => void
  queueHistorySnapshotBefore: (tabKey: string) => void
  markHistoryInitPending: (tabKey: string) => void
  markHistorySavePending: (tabKey: string) => void
  clearHistory: (tabKey: string) => void
  resetAllHistory: () => void
}

function isTabInEditMode(
  tabKey: string,
  localDraftsRef: MutableRefObject<Record<string, boolean>>,
  editModesRef: MutableRefObject<Record<string, boolean>>,
): boolean {
  const isLocalDraft = localDraftsRef.current[tabKey] || false
  return isLocalDraft || editModesRef.current[tabKey] || false
}

export function useTreatmentPlanHistory({
  formStates,
  formStatesRef,
  localDrafts,
  localDraftsRef,
  editModes,
  editModesRef,
  phaseVisibilityRef,
  setFormStates,
  setPhaseVisibility,
}: UseTreatmentPlanHistoryParams): UseTreatmentPlanHistoryResult {
  const [historyStates, setHistoryStates] = useState<
    Record<string, TxPlanHistoryState>
  >({})
  const pendingHistorySaveRef = useRef<string | null>(null)
  const pendingHistoryBeforeRef = useRef<{
    tabKey: string
    formState: TxPlanTabFormState
  } | null>(null)
  const pendingHistoryInitRef = useRef<string | null>(null)

  const pushHistorySnapshotFromState = useCallback(
    (tabKey: string, formState: TxPlanTabFormState): void => {
      if (!isTabInEditMode(tabKey, localDraftsRef, editModesRef)) {
        return
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        procedureModal: _procedureModal,
        ...stateWithoutModal
      } = formState
      const snapshot: FormStateSnapshot = {
        ...cloneTxPlanSnapshot(stateWithoutModal),
        procedureModal: null,
        phaseVisibility: { ...phaseVisibilityRef.current },
      }
      setHistoryStates((prev) => {
        const currentHistory = prev[tabKey] || { history: [], currentIndex: -1 }
        const newHistory =
          currentHistory.currentIndex < currentHistory.history.length - 1
            ? currentHistory.history.slice(0, currentHistory.currentIndex + 1)
            : currentHistory.history
        const updatedHistory = [...newHistory, snapshot]
        const limitedHistory =
          updatedHistory.length > TX_PLAN_HISTORY_MAX_SNAPSHOTS
            ? updatedHistory.slice(
                updatedHistory.length - TX_PLAN_HISTORY_MAX_SNAPSHOTS,
              )
            : updatedHistory
        return {
          ...prev,
          [tabKey]: {
            history: limitedHistory,
            currentIndex: limitedHistory.length - 1,
          },
        }
      })
    },
    [editModesRef, localDraftsRef, phaseVisibilityRef],
  )

  const saveHistorySnapshot = useCallback(
    (tabKey: string): void => {
      const formState = formStatesRef.current[tabKey]
      if (!formState) {
        return
      }
      pushHistorySnapshotFromState(tabKey, formState)
    },
    [formStatesRef, pushHistorySnapshotFromState],
  )

  const queueHistorySnapshotBefore = useCallback(
    (tabKey: string): void => {
      const formState = formStatesRef.current[tabKey]
      if (!formState) {
        return
      }
      if (!isTabInEditMode(tabKey, localDraftsRef, editModesRef)) {
        return
      }
      pendingHistoryBeforeRef.current = { tabKey, formState }
    },
    [editModesRef, formStatesRef, localDraftsRef],
  )

  const markHistoryInitPending = useCallback((tabKey: string): void => {
    pendingHistoryInitRef.current = tabKey
  }, [])

  const markHistorySavePending = useCallback((tabKey: string): void => {
    pendingHistorySaveRef.current = tabKey
  }, [])

  const canUndo = useCallback(
    (tabKey: string): boolean => {
      const historyState = historyStates[tabKey]
      if (!historyState) return false
      return historyState.currentIndex > 0
    },
    [historyStates],
  )

  const canRedo = useCallback(
    (tabKey: string): boolean => {
      const historyState = historyStates[tabKey]
      if (!historyState) return false
      return (
        historyState.currentIndex >= 0 &&
        historyState.currentIndex < historyState.history.length - 1
      )
    },
    [historyStates],
  )

  const restoreSnapshot = useCallback(
    (tabKey: string, snapshot: FormStateSnapshot, newIndex: number): void => {
      setFormStates((prev) => ({
        ...prev,
        [tabKey]: {
          ...cloneTxPlanSnapshot(snapshot),
          procedureModal: null,
        },
      }))
      if (snapshot.phaseVisibility) {
        setPhaseVisibility((prev) => ({
          ...prev,
          ...snapshot.phaseVisibility,
        }))
      }
      setHistoryStates((prev) => {
        const historyState = prev[tabKey]
        if (!historyState) return prev
        return {
          ...prev,
          [tabKey]: {
            ...historyState,
            currentIndex: newIndex,
          },
        }
      })
    },
    [setFormStates, setPhaseVisibility],
  )

  const handleUndo = useCallback(
    (tabKey: string): void => {
      const historyState = historyStates[tabKey]
      if (!historyState || historyState.currentIndex <= 0) {
        return
      }
      const newIndex = historyState.currentIndex - 1
      const snapshot = historyState.history[newIndex]
      restoreSnapshot(tabKey, snapshot, newIndex)
    },
    [historyStates, restoreSnapshot],
  )

  const handleRedo = useCallback(
    (tabKey: string): void => {
      const historyState = historyStates[tabKey]
      if (!historyState) {
        return
      }
      const newIndex = historyState.currentIndex + 1
      if (newIndex >= historyState.history.length) {
        return
      }
      const snapshot = historyState.history[newIndex]
      restoreSnapshot(tabKey, snapshot, newIndex)
    },
    [historyStates, restoreSnapshot],
  )

  const clearHistory = useCallback((tabKey: string): void => {
    setHistoryStates((prev) => {
      const newStates = { ...prev }
      delete newStates[tabKey]
      return newStates
    })
  }, [])

  const resetAllHistory = useCallback((): void => {
    setHistoryStates({})
    pendingHistorySaveRef.current = null
    pendingHistoryBeforeRef.current = null
    pendingHistoryInitRef.current = null
  }, [])

  useEffect(() => {
    if (pendingHistoryInitRef.current) {
      const tabKey = pendingHistoryInitRef.current
      const isEditMode = localDrafts[tabKey] || editModes[tabKey] || false
      if (isEditMode) {
        saveHistorySnapshot(tabKey)
        pendingHistoryInitRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editModes, localDrafts])

  useEffect(() => {
    const beforePending = pendingHistoryBeforeRef.current
    if (beforePending) {
      pendingHistoryBeforeRef.current = null
      pushHistorySnapshotFromState(
        beforePending.tabKey,
        beforePending.formState,
      )
    }
    const afterTabKey = pendingHistorySaveRef.current
    if (afterTabKey) {
      pendingHistorySaveRef.current = null
      saveHistorySnapshot(afterTabKey)
    }
  }, [formStates, pushHistorySnapshotFromState, saveHistorySnapshot])

  return {
    canUndo,
    canRedo,
    handleUndo,
    handleRedo,
    saveHistorySnapshot,
    queueHistorySnapshotBefore,
    markHistoryInitPending,
    markHistorySavePending,
    clearHistory,
    resetAllHistory,
  }
}
